import React, { Fragment, } from 'react';
import { useSelector } from 'react-redux';

// Domain imports.
import { Loader } from '@domain/components';
import { useSaveIncidentFactory, } from '@domain/hooks';
import { chooseLogoSize, checkIsFeedback, generatePageContent, getPrevNextPagePath } from '@domain/helpers';
import { companyNames, proccessType } from '@domain/constants'
import { validationSchema, validationSchemaNotRequired } from './validation-schema';
import { detectContentType } from '@domain/content';
import { useQuestionnaire } from '@domain/providers';
import routes from '../../routes';
import { useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { GuidedImagesUploadPage } from '@domain/pages';

function ImageUploads() {

  const { questionnaire } = useQuestionnaire();

  const incident = useSelector((state) => state.incident);
  const insurer = useSelector(state => state.insurer);
  const isVIP = insurer.objectType === proccessType.Casco && (insurer.brand === companyNames.VIP || !insurer.brand)
  const files = useSelector(state => state.files)

  const themeContext = useTheme();

  const location = useLocation()
  const currentPath = location.pathname;
  const extraDamageImge = currentPath.includes(routes.extraDamageImages)
  const images = extraDamageImge ? files.damageImages.filter(damageImg => damageImg.extraDamageImage) : files.damageImages.filter(damageImg => !damageImg.extraDamageImage)

  const page = questionnaire.find(page => page.path === currentPath)
  const saveBehavior = page ? page.saveBehavior : undefined

  const saveIncident = useSaveIncidentFactory(incident, saveBehavior);



  if (!images || !insurer || !incident || !questionnaire?.length || !files.filesFetched) {
    return <Loader />;
  }

  const isFeedback = checkIsFeedback(incident)

  const pagePath = getPrevNextPagePath(
    questionnaire,
    incident,
    currentPath,
    files,
    isFeedback
  );
  const nextPage = pagePath.nextPage !== currentPath
    ? pagePath.nextPage
    : undefined;


  const isPageBeforeSuccess = questionnaire[questionnaire.length - 2].path === currentPath

  function feedbackCause() {
    const page = questionnaire.find(page => page.name === 'guided_images')
    const feedbacktext = page?.feedbacktext ? decodeURIComponent(page?.feedbacktext) : null
    return feedbacktext
  }

  const contentType = detectContentType(insurer)
  const pageName = questionnaire.find(page => page.path === currentPath).name
  const content = generatePageContent(contentType, insurer, incident, false, false, pageName)

  const legend = content["over-title-text"]
  const title = content["title"]
  const mobileText = content["mobile-text"]
  const text = content["intro-text"]
  const secondText = content["second-text"]
  const customHelpLink = content["custom-help-link"]
  const helpLinkText = content["help-link-text"]
  const useCustomLogo = content["use-custom-logo"]
  const maxImgSize = content["max-img-size"]
  const maxNumberOfImages = content["max-number-images"]
  const instructions = [{ title: 'Overzichtsfoto linksvoor', explanation: 'Maak een overzichtfoto linksvoor waarop uw hele auto te zien is.', example: '1' }, { title: 'Overzichtsfoto linksachter', explanation: 'Maak een overzichtfoto linksachter waarop uw hele auto te zien is.', example: '2' }, { title: 'Overzichtsfoto rechtsachter', explanation: 'Maak een overzichtfoto rechtsachter waarop uw hele auto te zien is.', example: '3' }, { title: 'Overzichtsfoto rechtsvoor', explanation: 'Maak een overzichtfoto rechtsvoor waarop uw hele auto te zien is.', example: '4' }, { title: 'Overzichtsfoto schade', explanation: 'Maak een foto waarop de hele schade zichtbaar is.', example: '5' }, { title: 'Detailfoto(‘s) schade', explanation: 'Maak één of meerdere detailfoto’s van de schade vanuit een andere hoek.', example: '6' }]
  const firstFaqs = content["faqs"]
  const validationText = extraDamageImge ? 'Voeg alsjeblieft minimaal een foto toe' : content["validation-text"]

  const imagesWithPlaceholders = [...images, {}, {}, {}, {}, {}, {}].slice(0, 6)
  const imagesDisplayed = instructions.map((instruction) => {
    const img = imagesWithPlaceholders.find(img => img?.imageCategory === instruction.title)
    if (img) {
      return img
    }
    return {}
  })


  const faqs = firstFaqs.concat({
    question: "Hoe maak ik goede foto's?",
    answer: (
      <dl>
        {instructions.map(({ title, explanation }, i) => (
          <Fragment key={title}>
            <dt>
              {i + 1}. {title}
            </dt>
            <br />
            <dd>{explanation}</dd>
            <br />
            <br />
          </Fragment>
        ))}
      </dl>
    ),
  })


  const middleLogo = {
    src: insurer.logoURL || '',
    alt: insurer.brandName || 'uw verzekeraar',
    width: '100%',
    height: chooseLogoSize(themeContext.logoSize),
  }

  const validationSchemaToBeUsed = () => {
    if (incident.requiredPhotos === false && page.required === false) {
      return validationSchemaNotRequired(maxNumberOfImages)
    }
    const minFotos = extraDamageImge ? 1 : 6
    return validationSchema(maxNumberOfImages, validationText, minFotos)
  }

  return (
    <GuidedImagesUploadPage
      nextPage={nextPage}
      prevPage={pagePath.prevPage}
      questionnaire={questionnaire}
      saveIncident={saveIncident}
      buttonText="Volgende"
      currentPath={currentPath}
      instructions={instructions}
      filesSetName="damageImages"
      filesSet={images}
      feedbackCause={feedbackCause()}
      validationSchema={validationSchemaToBeUsed()}
      faqs={faqs}
      legend={legend}
      heading={title}
      mobileParagraph={mobileText}
      firstParagraph={text}
      secondParagraph={secondText}
      files={files}
      incident={incident}
      insurer={insurer}
      isPageBeforeSuccess={isPageBeforeSuccess}
      isVIP={isVIP}
      isFeedback={isFeedback}
      imagesDisplayed={imagesDisplayed}
      customHelpLink={customHelpLink}
      helpLinkText={helpLinkText}
      customMiddleLogo={useCustomLogo}
      middleLogo={middleLogo}
      maxImgSize={maxImgSize}
      maxNumberOfImages={maxNumberOfImages}
      extraDamageImage={extraDamageImge}
    />
  )
}

export default ImageUploads;
